import type { TenantEnvironment } from '@/api/superadmin';
import { CopyButton } from '@/components/primitives/copy-button';
import { SecretInput } from '@/components/primitives/secret-input';

function Field({ label, value, secret }: { label: string; value: string | null; secret?: boolean }) {
  if (!value) {
    return (
      <div className="flex flex-col gap-1">
        <span className="text-label-xs text-text-soft">{label}</span>
        <span className="text-paragraph-sm text-text-soft">Unavailable</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <span className="text-label-xs text-text-soft">{label}</span>
      {secret ? (
        <SecretInput
          value={value}
          /* Display only: the key is shown here, never edited from the console. */
          onChange={() => undefined}
          readOnly
          copyButton
          className="w-full"
        />
      ) : (
        <div className="bg-bg-weak flex items-center gap-2 rounded-md px-2 py-1.5">
          <code className="text-paragraph-xs text-text-sub min-w-0 flex-1 break-all">{value}</code>
          <CopyButton valueToCopy={value} size="xs" ariaLabel={`Copy ${label}`} />
        </div>
      )}
    </div>
  );
}

/**
 * One environment's identifiers.
 *
 * The API key is behind `SecretInput` rather than printed: this screen is the
 * one place in the product that shows another organization's key, and it is as
 * likely to be open on a shared screen as anywhere.
 */
export function EnvironmentKeysCard({ environment, name }: { environment: TenantEnvironment | null; name: string }) {
  return (
    <div className="border-stroke-soft bg-bg-white flex flex-col gap-4 rounded-xl border p-5">
      <h3 className="text-label-sm text-text-strong">{name}</h3>

      {!environment ? (
        <p className="text-paragraph-sm text-text-soft">This tenant has no {name.toLowerCase()} environment.</p>
      ) : (
        <>
          <Field label="Environment ID" value={environment._id} />
          <Field label="Application identifier" value={environment.identifier} />
          <Field label="API key" value={environment.apiKey} secret />
        </>
      )}
    </div>
  );
}
